import { Typography, FlyoutMenu, Button } from '@getsynapse/design-system';
import { useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import {
  RequestQuestion,
  dropdownOption,
  checkboxOption,
  radioOption,
  QuestionTypes,
} from 'utils/customTypes';
import { Question } from './Question';
import { cloneDeep, set } from 'lodash';
import textIcon from 'assets/icons/text-icon.svg';
import dropdownIcon from 'assets/icons/dropdown-icon.svg';
import radioIcon from 'assets/icons/radio-icon.svg';
import checkboxIcon from 'assets/icons/checkbox-icon.svg';
import toggleIcon from 'assets/icons/toggle-icon.svg';
import urlIcon from 'assets/icons/url-icon.svg';
import numericIcon from 'assets/icons/numeric-icon.svg';
import dateIcon from 'assets/icons/date-icon.svg';
import uploadIcon from 'assets/icons/upload-icon.svg';
import rangeIcon from 'assets/icons/range-icon.svg';
import { REQUEST_SECTIONS } from 'utils/constants';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import classnames from 'classnames';

type CustomQuestionsProps = {
  formQuestions: RequestQuestion[];
  setFormQuestions: React.Dispatch<React.SetStateAction<RequestQuestion[]>>;
  section: typeof REQUEST_SECTIONS[keyof typeof REQUEST_SECTIONS];
};

export const CustomQuestions = ({
  formQuestions,
  setFormQuestions,
  section,
}: CustomQuestionsProps) => {
  const [isSortEnabled, setIsSortEnabled] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const questions = useMemo(
    () =>
      formQuestions.filter(
        (question: RequestQuestion) => question && question.section === section
      ),
    [formQuestions, section]
  );

  const changeQuestionData = (
    path: string,
    value:
      | dropdownOption[]
      | radioOption[]
      | checkboxOption[]
      | string
      | boolean
      | number
      | null,
    questionId: string
  ) => {
    if (path === 'remove') {
      const updatedFormQuestions = formQuestions?.filter(
        (question) => question.id !== questionId
      );
      setFormQuestions(updatedFormQuestions!);
    } else {
      const updatedFormQuestions = formQuestions?.map((question) => {
        if (question.id === questionId) {
          const questionClone = cloneDeep(question);
          set(questionClone, path, value);
          return questionClone;
        }
        return question;
      });
      setFormQuestions(updatedFormQuestions!);
    }
  };

  const flyoutOptionClasses = 'hover:text-primary hover:bg-neutral-lightest';

  const addNewQuestion = (
    questionType: QuestionTypes,
    questionDefaultLabel: string
  ) => {
    const newQuestion = {
      type: questionType,
      data: {
        value: '',
        label: questionDefaultLabel,
        isRequired: true,
      },
      id: uuidv4(),
      section: section,
    };
    setFormQuestions((prevQuestions) => [...prevQuestions, newQuestion]);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setFormQuestions((questions) => {
        const questionsIds = questions.map((question) => question.id);
        const oldIndex = questionsIds.indexOf(active.id);
        const newIndex = questionsIds.indexOf(over.id);
        return arrayMove(questions, oldIndex, newIndex);
      });
    }
  };

  return (
    <div className='flex flex-col my-4'>
      <Button
        variant='tertiary'
        disabled={questions.length < 2}
        iconName='reorder-four-outline'
        onClick={() => setIsSortEnabled((prevState) => !prevState)}
        className={classnames('self-end', {
          'bg-neutral-lightest text-primary-dark': isSortEnabled,
        })}
      >
        {intl.get('SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.REORDER')}
      </Button>
      <Typography className='mt-2'>
        {intl.get('SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.TITLE')}
      </Typography>
      <div className='mb-3'>
        <Typography variant='caption' className='text-neutral'>
          {intl.get('SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.CAPTION1')}
        </Typography>
      </div>
      {questions && (
        <div className='grid grid-cols-2 gap-x-20 gap-y-6'>
          {isSortEnabled ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={questions} strategy={rectSortingStrategy}>
                {questions?.map((question) => (
                  <Question
                    questionData={question}
                    key={question.id}
                    changeQuestionData={changeQuestionData}
                    isSortEnabled={isSortEnabled}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            questions?.map((question) => (
              <Question
                questionData={question}
                key={question.id}
                changeQuestionData={changeQuestionData}
              />
            ))
          )}
        </div>
      )}
      <FlyoutMenu
        className='mt-6'
        options={[
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.TEXT'),
              src: textIcon,
              onClick: () => addNewQuestion('customTextArea', 'Text'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.TEXT'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.DROPDOWN'),
              src: dropdownIcon,
              onClick: () => addNewQuestion('dropdown', 'Dropdown'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.DROPDOWN'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.RADIO_BUTTON'),
              src: radioIcon,
              onClick: () => addNewQuestion('radio', 'Radio button'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.RADIO_BUTTON'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.CHECKBOX'),
              src: checkboxIcon,
              onClick: () => addNewQuestion('checkbox', 'Checkbox'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.CHECKBOX'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.TOGGLE'),
              src: toggleIcon,
              onClick: () => addNewQuestion('toggle', 'Toggle'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.TOGGLE'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.URL'),
              src: urlIcon,
              onClick: () => addNewQuestion('url', 'URL'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.URL'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.NUMBER'),
              src: numericIcon,
              onClick: () => addNewQuestion('number', 'Number'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.NUMBER'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.DATE'),
              src: dateIcon,
              onClick: () => addNewQuestion('date', 'Date'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.DATE'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.FILE'),
              src: uploadIcon,
              onClick: () => addNewQuestion('file', 'File'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.FILE'),
          },
          {
            icon: {
              description: intl.get('CUSTOM_QUESTIONS.RANGE'),
              src: rangeIcon,
              onClick: () => addNewQuestion('range', 'Range'),
              className: flyoutOptionClasses,
            },
            label: intl.get('CUSTOM_QUESTIONS.RANGE'),
          },
        ]}
        label={intl.get(
          'SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.ADD_QUESTION'
        )}
      />
    </div>
  );
};

export default CustomQuestions;
